import React from 'react'
import queryString from 'query-string'
import { useHistory } from 'react-router-dom'

import { ZariContext } from '_zari/context'

import { Button, Col, Row, Portlet, Table } from '_zari/core'

import * as CodigosTransaccionesConstant from 'constants/codigosTransacciones'

import * as InventariosService from 'services/inventario.service'

import * as FormGroups from './formGroups'
import { DownloadAxiosFile } from 'utils'
import { RETIRO_PARA_ENTREGA_A_TRABAJADORES } from 'constants/tiposOperacionSunat'

const portletBreadcrumbs = [{ name: 'Entradas - Salida' }]

const InventarioEntradaSalidaFormGroup = ({ location }) => {
  const __zariContext = React.useContext(ZariContext)

  const history = useHistory()

  const tablaRef = React.useRef(null)

  const parsedParams = React.useMemo(() => queryString.parse(location.search), [location])

  const [seInventarioTipo, setSeInventarioTipo] = React.useState(parsedParams.t || '')
  const [tipoOperacion, setTipoOperacion] = React.useState(parsedParams.to || '')

  const onChangeSeInventarioTipo = React.useCallback(
    e => {
      setSeInventarioTipo(e.target.value)

      const queryParams = queryString.stringify({
        t: e.target.value,
        to: ''
      })

      history.push(`/${CodigosTransaccionesConstant.INVENTARIOS_ENTRADASALIDA_LISTA}?${queryParams}`)
    },
    [history]
  )

  const onChangeTipoOperacion = React.useCallback(
    e => {
      setTipoOperacion(e.target.value)

      const queryParams = queryString.stringify({
        t: seInventarioTipo,
        to: e.target.value
      })

      history.push(`/${CodigosTransaccionesConstant.INVENTARIOS_ENTRADASALIDA_LISTA}?${queryParams}`)
    },
    [history, seInventarioTipo]
  )

  const clickBuscarHandler = React.useCallback(() => {
    tablaRef.current.reload({
      tipoOperacion: tipoOperacion
    })
  }, [tipoOperacion])

  const clickEliminar = React.useCallback(
    async ({ _id }) => {
      await InventariosService.eliminarxId(_id)
        .then(() => {
          __zariContext.snack('success', 'Registro eliminado.')
          tablaRef.current.reload()
        })
        .catch(({ data: { error } }) => __zariContext.snack('error', error))
    },
    [__zariContext]
  )

  const clickDescargar = React.useCallback(async ({ _id }) => {
    // Para salidas de tipo 36 (retiro para entrega a trabajadores) usamos la misma
    // Hoja de Entrega que se genera al registrar la salida — así el formato que ve
    // el usuario después coincide con el original.
    const usarHojaEntrega = tipoOperacion === RETIRO_PARA_ENTREGA_A_TRABAJADORES
    const descarga = usarHojaEntrega
      ? InventariosService.descargarHojaEntregaxId(_id)
      : InventariosService.descargarxId(_id)

    await descarga.then(({ data, headers }) => {
      DownloadAxiosFile.downloadAsBlob(data, headers)
    })
  }, [tipoOperacion])

  const clickEditar = React.useCallback(
    ({ _id }) => {
      if (!seInventarioTipo) {
        __zariContext.snack('warning', 'Seleccionar tipo')
        return
      }

      if (!tipoOperacion) {
        __zariContext.snack('warning', 'Seleccionar tipo operacion')
        return
      }

      const queryParams = queryString.stringify({
        t: seInventarioTipo,
        to: tipoOperacion
      })

      history.push(`/${CodigosTransaccionesConstant.INVENTARIOS_ENTRADASALIDA_NUEVO}/${_id}?${queryParams}`)
    },
    [history, __zariContext, seInventarioTipo, tipoOperacion]
  )

  const clickNuevo = React.useCallback(() => {
    if (!seInventarioTipo) {
      __zariContext.snack('warning', 'Seleccionar tipo')
      return
    }

    if (!tipoOperacion) {
      __zariContext.snack('warning', 'Seleccionar tipo operacion')
      return
    }

    const queryParams = queryString.stringify({
      t: seInventarioTipo,
      to: tipoOperacion
    })

    history.push(`/${CodigosTransaccionesConstant.INVENTARIOS_ENTRADASALIDA_NUEVO}?${queryParams}`)
  }, [history, __zariContext, seInventarioTipo, tipoOperacion])

  return (
    <>
      <Portlet>
        <Portlet.Header
          title='Inventarios'
          breadcrumbs={portletBreadcrumbs}
          buttons={[{ name: 'Nuevo', onClick: clickNuevo }]}
        />
        <Portlet.Body>
          <Row>
            <Col md={4}>
              <FormGroups.SalidaEntradaTipoFormGroup value={seInventarioTipo} onChange={onChangeSeInventarioTipo} />
            </Col>
            <Col md={4}>
              <FormGroups.TipoOperacionFormGroup
                value={tipoOperacion}
                onChange={onChangeTipoOperacion}
                vSeInventario={seInventarioTipo}
              />
            </Col>
            <Col md={4}>
              <Button onClick={clickBuscarHandler} block>
                Buscar
              </Button>
            </Col>
          </Row>
          <Row className='mt-3'>
            <Col md={12}>
              <Table.ServerSide
                useNest={true}
                ref={tablaRef}
                baseParams={{
                  tipoConsulta: 'seInventarios',
                  tipoOperacion: tipoOperacion
                }}
                url='/kardex'
                headRows={[{ label: 'TITULO' }, { label: 'FECHA' }, { label: 'DOCUMENTO' }, { label: '' }]}
                rowTemplate={row => (
                  <Table.Row hover key={row._id}>
                    <Table.Cell>{row.titulo || '--'}</Table.Cell>
                    <Table.Cell>{row.fechaEmision || '--'}</Table.Cell>
                    <Table.Cell>{`${row.tipoDocumento} - ${row.serieDocumento} - ${row.numeroDocumento}`}</Table.Cell>

                    <Table.Cell>
                      <Table.Dropdown>
                        {({ handleClose }) => [
                          <Table.Dropdown.Item
                            key='editar'
                            handleClose={handleClose}
                            onClick={e => clickEditar({ _id: row._id })}
                          >
                            Editar
                          </Table.Dropdown.Item>,
                          <Table.Dropdown.Item
                            key='eliminar'
                            handleClose={handleClose}
                            onClick={e => clickEliminar({ _id: row._id })}
                          >
                            Eliminar
                          </Table.Dropdown.Item>,
                          ...(seInventarioTipo === 'S'
                            ? [
                                <Table.Dropdown.Item
                                  key='descargar'
                                  handleClose={handleClose}
                                  onClick={e => clickDescargar({ _id: row._id })}
                                >
                                  Descargar
                                </Table.Dropdown.Item>
                              ]
                            : [])
                        ]}
                      </Table.Dropdown>
                    </Table.Cell>
                  </Table.Row>
                )}
              />
            </Col>
          </Row>
        </Portlet.Body>
      </Portlet>
    </>
  )
}

export default InventarioEntradaSalidaFormGroup
